import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Home, Calendar, Clock, ArrowLeft, ArrowRight, BookOpen, TrendingUp, AlertCircle, DollarSign, Share2, User } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import { publicApi } from '../utils/api';
import { setPageSeo, resetPageSeo } from '../utils/seo';

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true
});

const categoryIcons = {
  'Education': BookOpen,
  'News': TrendingUp,
  'Tips': AlertCircle,
  'Tips & Guides': AlertCircle,
  'Analysis': DollarSign,
  'Rate Updates': TrendingUp,
  'default': BookOpen
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    if (!post) return;
    const url = `${window.location.origin}/blog/${post.slug}`;
    setPageSeo({
      title: `${post.title} | My Mortgage Canada`,
      description: post.excerpt || '',
      canonical: url,
      image: post.image_url || undefined,
      type: 'article',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.excerpt || '',
        image: post.image_url || undefined,
        datePublished: post.created_at,
        dateModified: post.updated_at,
        author: { '@type': 'Organization', name: post.author_name || 'My Mortgage Canada' },
        publisher: { '@type': 'Organization', name: 'My Mortgage Canada' },
        mainEntityOfPage: url,
      },
    });
    return () => resetPageSeo();
  }, [post]);


  // Parse content - handles both markdown and HTML
  const parsedContent = useMemo(() => {
    if (!post?.content) return '';
    
    const content = post.content;
    
    // Check if content is already HTML (contains HTML tags)
    const isHtml = /<[a-z][\s\S]*>/i.test(content);
    
    if (isHtml) {
      // Content is already HTML, return as-is
      return content;
    } else {
      // Content is markdown, parse it
      return marked.parse(content);
    }
  }, [post?.content]);

  const fetchPost = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Add timestamp to prevent browser caching
      // Use publicApi for unauthenticated requests
      const timestamp = Date.now();
      const response = await publicApi.get(`/api/blog/posts/${slug}?_t=${timestamp}`);
      setPost(response.data);
      
      // Fetch related posts
      const allPostsRes = await publicApi.get(`/api/blog/posts?published_only=true&_t=${timestamp}`);
      const related = (allPostsRes.data || [])
        .filter(p => p.slug !== slug && p.category === response.data.category)
        .slice(0, 3);
      setRelatedPosts(related);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-CA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="pt-24 flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="text-2xl font-heading font-bold mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const CategoryIcon = categoryIcons[post.category] || categoryIcons.default;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-20 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
            <span>/</span>
            <span className="text-foreground truncate max-w-[200px]">{post.title}</span>
          </nav>
        </div>
      </div>
      
      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
              <CategoryIcon className="h-4 w-4" />
              {post.category}
            </span>
            {post.featured && (
              <span className="px-3 py-1 bg-yellow-500/10 text-yellow-500 rounded-full text-sm font-medium">
                Featured
              </span>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            {post.title}
          </h1>
          
          <p className="text-lg text-muted-foreground mb-6">
            {post.excerpt}
          </p>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              {post.author_name || 'My Mortgage Canada'}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {post.read_time || '5 min read'}
            </span>
            <button 
              onClick={handleShare}
              className="flex items-center gap-1.5 hover:text-primary transition-colors ml-auto"
            >
              <Share2 className="h-4 w-4" />
              Share
            </button>
          </div>
        </header>
        
        {/* Featured Image */}
        {post.image_url && (
          <div className="aspect-video rounded-xl overflow-hidden mb-8">
            <img 
              src={post.image_url} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {/* Article Content */}
        <div 
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parsedContent) }}
        />
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-8 pt-8 border-t border-border">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span 
                  key={tag}
                  className="px-3 py-1 bg-card border border-border rounded-full text-sm text-muted-foreground"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Navigation */}
        <div className="mt-8 pt-8 border-t border-border flex justify-between">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>
        </div>
      </article>
      
      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-card border-t border-border py-12">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-heading font-semibold mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => {
                const RelatedIcon = categoryIcons[relatedPost.category] || categoryIcons.default;
                return (
                  <Link
                    key={relatedPost.post_id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-background border border-border rounded-xl p-4 hover:border-primary/50 transition-all"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                        <RelatedIcon className="h-3 w-3" />
                        {relatedPost.category}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold group-hover:text-primary transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
      
      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              <span className="font-heading font-semibold">My Mortgage Canada</span>
            </Link>
            <p className="text-sm text-muted-foreground">
              &copy; 2025 My Mortgage Canada. Empowering Canadian homeowners.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
